import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@material-ui/core';
import { CardGiftcard, Close } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import ShareEarnModal from '../share-earn-modal/ShareEarnModal';
import { trackReferralFunnel } from '../../utils/tracking';
import { getExperimentPayload, trackExperimentExposure } from '../../utils/experiments';

const DISMISSAL_STORAGE_KEY = 'referralPromptDismissedAt';
const DAY_MS = 24 * 60 * 60 * 1000;

// Defaults = the prompt's shipped behavior. The `referral-prompt` experiment's
// CONTROL payload encodes exactly these, so with the experiment flag off/dark
// (getExperimentPayload → control) the gate is byte-identical to today.
const DEFAULT_MIN_SCORE_TO_PROMPT = 70;
const DEFAULT_DISMISSAL_TTL_DAYS = 14;

export const hasActivePaidSubscription = (userPackages, now = new Date()) => (
  Array.isArray(userPackages) && userPackages.some((item) => (
    item?.status === 'ACTIVE' && new Date(item?.subscriptionEndDt) >= now
  ))
);

// Prompt only at a moment of delight (strong score), only for free students
// (paying subscribers should not see promos), and never within the snooze
// window after a dismissal. subscriptionsLoaded must be true so Pro users
// never see the prompt flash while their plan is still being fetched.
// minScoreToPrompt / dismissalTtlDays come from the experiment payload; the
// defaults reproduce the shipped 70 / 14-day behavior for control and flag-off.
export const shouldShowReferralPrompt = ({
  score,
  role,
  dismissedAt,
  subscriptionsLoaded,
  hasActivePlan,
  minScoreToPrompt = DEFAULT_MIN_SCORE_TO_PROMPT,
  dismissalTtlDays = DEFAULT_DISMISSAL_TTL_DAYS,
  now = Date.now(),
}) => {
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore) || numericScore < minScoreToPrompt) {
    return false;
  }
  if (role === 'ROLE_ADMIN' || role === 'ROLE_TUTOR') {
    return false;
  }
  if (!subscriptionsLoaded || hasActivePlan) {
    return false;
  }
  if (dismissedAt && now - Number(dismissedAt) < dismissalTtlDays * DAY_MS) {
    return false;
  }
  return true;
};

const ReferralSharePrompt = ({ score }) => {
  const [dismissed, setDismissed] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const userPackages = useSelector((state) => state.userPackage?.userPackage);

  // The unit for the `referral-prompt` A/B is the logged-in user. localStorage
  // 'userId' is the user UUID login stores (= the units producer's canonical
  // user id), so client assignment and the offline analysis agree on the arm.
  const userId = globalThis.localStorage.getItem('userId');

  // Config payload (schemaVersion-2). Control = { minScoreToPrompt: 70,
  // dismissalTtlDays: 14 }; getExperimentPayload resolves control whenever the
  // master flag is off, the experiment is not active, or there is no userId, so
  // the gate below is unchanged from today until the experiment goes live.
  const {
    minScoreToPrompt = DEFAULT_MIN_SCORE_TO_PROMPT,
    dismissalTtlDays = DEFAULT_DISMISSAL_TTL_DAYS,
  } = getExperimentPayload('referral-prompt', userId);

  // Exposure fires when a logged-in user reaches the exam-result surface — the
  // pre-treatment point both arms share, INDEPENDENT of whether the prompt then
  // shows (the treatment moves that, so gating exposure on it would bias the
  // readout). No-ops while the experiment is dark. Mirrors the create-quiz
  // (#452) pattern; dedups per (experiment, unit) client-side.
  useEffect(() => {
    if (userId) {
      trackExperimentExposure('referral-prompt', userId);
    }
  }, [userId]);

  const visible = !dismissed && shouldShowReferralPrompt({
    score,
    role: globalThis.localStorage.getItem('role'),
    dismissedAt: globalThis.localStorage.getItem(DISMISSAL_STORAGE_KEY),
    subscriptionsLoaded: Array.isArray(userPackages),
    hasActivePlan: hasActivePaidSubscription(userPackages),
    minScoreToPrompt,
    dismissalTtlDays,
  });

  useEffect(() => {
    if (visible) {
      trackReferralFunnel('referral_prompt_shown', { surface: 'exam_result', score: Number(score) });
    }
    // Track once per result page view, not on every re-render state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!visible) {
    return null;
  }

  const handleShare = () => {
    trackReferralFunnel('referral_prompt_clicked', { surface: 'exam_result' });
    setShareModalOpen(true);
  };

  const handleDismiss = () => {
    trackReferralFunnel('referral_prompt_dismissed', { surface: 'exam_result' });
    globalThis.localStorage.setItem(DISMISSAL_STORAGE_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <>
      <Box
        data-testid="referral-share-prompt"
        style={{
          // Theme surface + text via the theme CSS vars (set on document.body by
          // setTheme), mirroring ShareEarnModal (#379). The old hardcoded #e8f5e9
          // stayed light-green in dark mode (~1.1:1 → effectively invisible),
          // which attenuated the live #569 treatment for dark-mode users.
          // --list-bkg + --text-color clear WCAG AA 4.5:1 in BOTH themes
          // (light #000-on-#e0e0e0 ≈ 15.9:1; dark #fff-on-#6d6d6d ≈ 5.2:1).
          display: 'flex', alignItems: 'center', gap: 12,
          // flexWrap lets the Share button (and dismiss) drop to their own line
          // at ~390px so the copy is never clipped; both stay reachable.
          flexWrap: 'wrap',
          background: 'var(--list-bkg, #e8f5e9)',
          color: 'var(--text-color, #000)',
          border: '1px solid var(--border-color, transparent)',
          borderRadius: 8,
          padding: '10px 16px', margin: '16px 0'
        }}
      >
        <Typography variant="body2" style={{ flex: '1 1 240px', color: 'inherit' }}>
          <strong>Nice work — {Number(score)}% correct!</strong> Know someone else prepping?
          Share QLearn and earn free Pro time.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<CardGiftcard />}
          onClick={handleShare}
        >
          Share & Earn
        </Button>
        <IconButton
          size="small"
          aria-label="Dismiss share prompt"
          onClick={handleDismiss}
          style={{ color: 'var(--text-color, rgba(0,0,0,0.54))' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <ShareEarnModal open={shareModalOpen} onClose={() => setShareModalOpen(false)} />
    </>
  );
};

export default ReferralSharePrompt;
